import CreativeGridView from '@/Enums/CampaignManagement/CreativeGridView.enum';
import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import CreativesTableColumns from '@/Enums/Creatives/CreativesTableColumns.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import ItemStatus from '@/Enums/ItemStatus.enum';
import { ILineItemCreative } from '@/Interfaces/CampaignManagement/LineItem.interface';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import TableActionButton from '@/components/Buttons/TableActionButton';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { numberToPercent } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { TextField } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { type Moment } from 'moment';
import { ChangeEvent } from 'react';
import { FieldErrors } from 'react-hook-form';
import CheckboxCell from './components/CheckboxCell';
import ClickUrlCell from './components/ClickUrlCell';
import PreviewCell from './components/PreviewCell';
import RemoveButton from './components/RemoveButton';

interface IStartEndDate {
  startDate: string | Moment | null;
  endDate: string | Moment | null;
}

interface IGetColumnsProps {
  view?: CreativeGridView;
  creativeMinAssociateDate?: string;
  creativeMaxAssociateDate?: string;
  handleFieldChange?: (
    id: number,
    field: keyof Pick<ILineItemCreative, 'associationStatus' | 'startDate' | 'endDate' | 'clickUrl' | 'disassociated'>,
  ) => (value: ItemStatus | string | null | boolean | Moment) => void;
  handleRelativeWeightChange?: (id: number) => (event: ChangeEvent<HTMLInputElement>) => void;
  handleViewCreative?: (row: ICreative) => void;
  handleViewPixels?: (index: number) => void;
  creativeIds?: number[];
  formErrors?: FieldErrors;
}

export const getColumns = ({
  view = CreativeGridView.LineItemForm,
  creativeMinAssociateDate,
  creativeMaxAssociateDate,
  handleFieldChange,
  handleRelativeWeightChange,
  handleViewCreative,
  handleViewPixels,
  creativeIds,
  formErrors,
}: IGetColumnsProps): GridColDef[] => {
  const isSummaryView = view === CreativeGridView.LineItemSummary;

  const baseColumns = [
    isSummaryView ? null : CheckboxCell,
    {
      field: CreativesTableColumns.CREATIVE_NAME,
      headerName: 'Creative Name',
      flex: 2,
      display: 'flex',
      renderCell: ({ row: { disassociated }, value }: GridRenderCellParams<ILineItemCreative, string>) => (
        <span style={{ textDecoration: disassociated ? 'line-through' : 'none' }}>{value || '—'}</span>
      ),
    },
    {
      field: CreativesTableColumns.CREATIVE_ID,
      headerName: 'Creative ID',
      flex: 0.5,
      align: 'center',
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams<ILineItemCreative, number>) => value || '—',
    },
    {
      field: CreativesTableColumns.THUMBNAIL,
      headerName: 'Preview',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      sortable: false,
      display: 'flex',
      renderCell: ({ row }: GridRenderCellParams<ICreative>) => (
        <PreviewCell row={row} handleViewCreative={handleViewCreative} />
      ),
    },
    {
      field: CreativesTableColumns.TYPE,
      headerName: 'Type',
      align: 'center',
      headerAlign: 'center',
      flex: 1.1,
      display: 'flex',
      renderCell: ({ value }: GridRenderCellParams<ILineItemCreative, CreativeType>) => {
        const Icon = CREATIVE_TYPES_ICONS[value!];
        return (
          <div className='flex justify-center items-center'>
            <Icon className={'w-4 mr-1'} />
            <span>{CREATIVE_TYPES_TITLES[value!]}</span>
          </div>
        );
      },
    },
  ].filter(Boolean) as GridColDef[];

  if (view === CreativeGridView.LineItemModal) {
    return baseColumns;
  }

  baseColumns.push(
    {
      field: 'thirdPartyPixels',
      sortable: false,
      headerName: '3rd Party Pixels',
      align: 'center',
      flex: 0.9,
      renderCell: ({ row }: GridRenderCellParams<ILineItemCreative & { creativeIndex: number }>) => (
        <TableActionButton variant='outlined' color='primary' onClick={() => handleViewPixels?.(row.creativeIndex)}>
          <span>View Pixels</span>
        </TableActionButton>
      ),
    },
    {
      field: 'associationWeightRel',
      sortable: false,
      headerName: 'Relative Weight*',
      align: 'center',
      flex: 0.8,
      display: 'flex',
      renderCell: ({ row: { id, disassociated }, value }: GridRenderCellParams<ILineItemCreative, string>) => {
        if (isSummaryView) {
          return <span>{value || '—'}</span>;
        }

        const handleChange = handleRelativeWeightChange!(id);
        const message = !value && 'Must be > 1';
        return (
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            value={value}
            onChange={handleChange}
            error={!!message}
            helperText={message}
            disabled={disassociated}
            slotProps={{
              input: {
                style: {
                  fontWeight: 600,
                },
                sx: {
                  height: '40px',
                },
              },
            }}
          />
        );
      },
    },
    {
      field: 'associationWeightPct',
      sortable: false,
      headerName: 'Percentage Weight',
      align: 'center',
      flex: 0.8,
      valueGetter: (value: number) => numberToPercent(value),
    },
    {
      field: 'clickUrl',
      sortable: false,
      headerName: 'Click Url',
      align: 'center',
      flex: 1.5,
      display: 'flex',
      renderCell: ({ row: { id, type, disassociated }, value }: GridRenderCellParams<ILineItemCreative, string>) => (
        <ClickUrlCell
          id={id}
          type={type}
          value={value ?? ''}
          errors={(formErrors?.creatives || []) as FieldErrors[]}
          creativeIds={creativeIds || []}
          onChange={handleFieldChange!}
          disabled={disassociated || isSummaryView}
        />
      ),
    },
    {
      field: 'startDate',
      sortable: false,
      headerName: 'Start Date',
      align: 'center',
      headerAlign: 'center',
      flex: 1.3,
      valueGetter: (_value: unknown, row: ILineItemCreative): IStartEndDate =>
        row ? { startDate: row.startDate, endDate: row.endDate } : { startDate: null, endDate: null },
      display: 'flex',
      renderCell: ({
        row: { id, disassociated },
        value: { startDate, endDate } = { startDate: null, endDate: null },
      }: GridRenderCellParams<ILineItemCreative, IStartEndDate>) => {
        if (isSummaryView) {
          return <span>{startDate ? moment(startDate).format(DateFormat.NumericMonthDayYearTime) : '—'}</span>;
        }
        const handleChange = handleFieldChange!(id, 'startDate');

        return (
          <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <MobileDateTimePicker
              timezone='UTC'
              value={startDate}
              referenceDate={moment(creativeMinAssociateDate)}
              format={DateFormat.NumericMonthDayYearTime}
              onChange={handleChange}
              minDateTime={creativeMinAssociateDate}
              // @ts-expect-error does not recognize a moment object as a valid value
              maxDateTime={creativeMaxAssociateDate || endDate}
              disabled={disassociated}
              sx={{
                '& .MuiInputBase-root': {
                  height: '40px',
                  fontWeight: 600,
                  fontSize: '14px',
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
    {
      field: 'endDate',
      sortable: false,
      headerName: 'End Date',
      align: 'center',
      headerAlign: 'center',
      flex: 1.3,
      valueGetter: (_value: unknown, row: ILineItemCreative): IStartEndDate =>
        row ? { startDate: row.startDate, endDate: row.endDate } : { startDate: null, endDate: null },
      display: 'flex',
      renderCell: ({
        row: { id, disassociated },
        value: { startDate, endDate } = { startDate: null, endDate: null },
      }: GridRenderCellParams<ILineItemCreative, IStartEndDate>) => {
        if (isSummaryView) {
          return <span>{endDate ? moment(endDate).format(DateFormat.NumericMonthDayYearTime) : '—'}</span>;
        }
        const handleChange = handleFieldChange!(id, 'endDate');

        return (
          <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
            <MobileDateTimePicker
              timezone='UTC'
              value={endDate}
              referenceDate={startDate ? moment(startDate) : moment(creativeMaxAssociateDate)}
              format={DateFormat.NumericMonthDayYearTime}
              onChange={handleChange}
              // @ts-expect-error does not recognize a moment object as a valid value
              minDateTime={startDate}
              maxDateTime={creativeMaxAssociateDate}
              disabled={!startDate || disassociated || isSummaryView}
              sx={{
                '& .MuiInputBase-root': {
                  height: '40px',
                  fontWeight: 600,
                  fontSize: '14px',
                },
              }}
            />
          </LocalizationProvider>
        );
      },
    },
  );

  if (isSummaryView) {
    return baseColumns;
  }

  return [
    ...baseColumns,
    {
      field: 'action',
      headerName: 'Action',
      align: 'center',
      headerAlign: 'center',
      flex: 0.75,
      sortable: false,
      display: 'flex',
      renderCell: ({ row: { id, disassociated } }: GridRenderCellParams<ILineItemCreative>) =>
        disassociated ? '-' : <RemoveButton onRemove={() => handleFieldChange!(id, 'disassociated')(true)} />,
    },
  ];
};
